import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Pickaxe, Layers, Compass, MapPin, ShieldCheck, ClipboardCheck, Coins, Users, Trees,
  Briefcase, TrendingUp, Sparkles, BookCheck, Network, BadgeCheck, Trophy, Download,
  PlayCircle, Loader2, CheckCircle2, XCircle, ToggleRight, ToggleLeft,
} from "lucide-react";

const STREAMS = [
  { key: "rights", label: "Mineral Rights", icon: Pickaxe },
  { key: "geological", label: "Geological", icon: Layers },
  { key: "exploration", label: "Exploration", icon: Compass },
  { key: "spatial", label: "Mining GIS", icon: MapPin },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "inspection", label: "Inspection & Field", icon: ClipboardCheck },
  { key: "revenue", label: "Revenue & Royalties", icon: Coins },
  { key: "asm", label: "ASM360", icon: Users },
  { key: "esg", label: "ESG & Closure", icon: Trees },
  { key: "investment", label: "Investment360", icon: Briefcase },
  { key: "executive", label: "Executive Mining", icon: TrendingUp },
  { key: "ai", label: "AI Mining", icon: Sparkles },
  { key: "standards", label: "International Standards", icon: BookCheck },
  { key: "integration", label: "Cross-Domain", icon: Network },
  { key: "acceptance", label: "Acceptance & Benchmark", icon: BadgeCheck },
];

const Section = ({ title, children }) => (
  <div className="cad-panel p-4">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">{title}</div>
    <div className="mt-3 space-y-1">{children}</div>
  </div>
);
const KV = ({ k, v }) => (
  <div className="border-b cad-hairline pb-1 flex items-center gap-2 text-[12px]">
    <div className="font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5] flex-1 truncate">{k}</div>
    <div className="font-mono-cad text-[#F6F8FA] break-all text-right max-w-[65%]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
  </div>
);

const StreamPanel = ({ streamKey }) => {
  const q = useQuery({ queryKey: ["mineral-" + streamKey], queryFn: async () => (await api.get("/cadastre360/mineral/" + streamKey)).data });
  if (q.isLoading) return <div className="cad-panel p-6 text-[#B3C2CC] text-[13px]">Loading…</div>;
  if (q.isError) return <div className="cad-panel p-6 text-[#E67E7E] text-[13px]">Stream unavailable</div>;
  const d = q.data || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={"mineral-stream-" + streamKey}>
      {Object.entries(d).map(([key, val]) => {
        if (key === "generated_at") return null;
        if (Array.isArray(val)) {
          return (
            <Section key={key} title={key.replace(/_/g, " ")}>
              <div className="space-y-1 max-h-[320px] overflow-y-auto">
                {val.slice(0, 30).map((item, i) => (
                  <div key={key + "-" + i} className="border-b cad-hairline pb-1 text-[11px] text-[#DCE3E8]">
                    {typeof item === "object" ? (
                      <div className="grid grid-cols-2 gap-1">
                        {Object.entries(item).slice(0, 8).map(([kk, vv]) => (
                          <div key={kk} className="truncate"><span className="font-mono-cad text-[10px] text-[#7C93A5]">{kk}</span> <span className="text-[#F6F8FA]">{typeof vv === "object" ? JSON.stringify(vv) : String(vv)}</span></div>
                        ))}
                      </div>
                    ) : String(item)}
                  </div>
                ))}
                {val.length > 30 && <div className="text-[10px] font-mono-cad text-[#7C93A5]">…{val.length - 30} more</div>}
              </div>
            </Section>
          );
        }
        if (typeof val === "object" && val !== null) {
          return (
            <Section key={key} title={key.replace(/_/g, " ")}>
              {Object.entries(val).slice(0, 30).map(([kk, vv]) => <KV key={kk} k={kk} v={vv} />)}
            </Section>
          );
        }
        return (
          <div key={key} className="cad-panel px-3 py-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{key.replace(/_/g, " ")}</div>
            <div className="font-display font-bold text-[#F6F8FA] mt-0.5">{typeof val === "boolean" ? (val ? "yes" : "no") : String(val)}</div>
          </div>
        );
      })}
    </div>
  );
};

const AcceptancePanel = () => {
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ["mineral-flags"], queryFn: async () => (await api.get("/cadastre360/mineral/feature-flags")).data });
  const reports = useQuery({ queryKey: ["mineral-acc-reports"], queryFn: async () => (await api.get("/cadastre360/mineral/acceptance/reports")).data });
  const bench = useQuery({ queryKey: ["mineral-bench"], queryFn: async () => (await api.get("/cadastre360/mineral/global-mining-benchmark")).data });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/mineral/acceptance/run", {})).data,
    onSuccess: (d) => { toast.success("Mining " + d.overall + " · " + d.passed + "/" + d.scope_size); qc.invalidateQueries({ queryKey: ["mineral-acc-reports"] }); },
  });
  const toggle = useMutation({
    mutationFn: async ({ name, enabled }) => (await api.post("/cadastre360/mineral/feature-flags/" + name, { enabled })).data,
    onSuccess: () => { toast.success("Flag updated"); qc.invalidateQueries({ queryKey: ["mineral-flags"] }); },
  });
  const latest = (reports.data || [])[0];
  const b = bench.data;
  return (
    <div className="space-y-3">
      {b && (
        <div className="cad-panel p-6 flex items-center gap-6">
          <div className="text-[64px] font-display font-bold text-[#C99A2E] leading-none">{b.cadastre360?.digital_maturity}%</div>
          <div>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Global mining maturity</div>
            <div className="font-display text-2xl font-bold text-[#F6F8FA] mt-1">Peer percentile {b.peer_percentile}</div>
            <div className="text-[12px] text-[#B3C2CC] mt-1">Benchmarked against Australia (WA), Canada (BC), Chile, Botswana, SA, DRC, Ghana, Zambia</div>
          </div>
        </div>
      )}
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[220px] leading-relaxed">
          Run mining governance acceptance across all 14 streams and download the sovereign Mining Governance Readiness Certificate.
        </div>
        <button onClick={() => run.mutate()} disabled={run.isPending}
                className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
                data-testid="mineral-acc-run">
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run mining acceptance
        </button>
        <a href={API + "/cadastre360/mineral/acceptance/certificate.pdf"} target="_blank" rel="noreferrer"
           className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
           data-testid="mineral-acc-cert">
          <Download size={11} /> Mining Governance Readiness Certificate (PDF)
        </a>
      </div>
      {latest && (
        <div className="cad-panel p-4">
          <div className="flex items-center gap-3">
            <span className={"cad-chip text-[10px] uppercase " + (latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red")}>{latest.overall}</span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.id}</span>
            <span className="font-mono-cad text-[11px] text-[#B3C2CC]">{latest.passed}/{latest.scope_size}</span>
            <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">{latest.run_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1">
            {(latest.checks || []).map((c, i) => (
              <div key={c.stream + "-" + i} className="border-b cad-hairline pb-1 flex items-start gap-2">
                {c.ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div>
                  <div className="text-[11px] text-[#F6F8FA]">{c.label}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono-cad">{c.note}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono-cad text-[#7C93A5] break-all">signature: {latest.signature}</div>
        </div>
      )}
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Stream feature flags</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {Object.entries(flags.data || {}).map(([k, v]) => (
            <div key={k} className="border-b cad-hairline pb-1 flex items-center gap-2">
              <div className="text-[11px] text-[#F6F8FA] flex-1 font-mono-cad">{k.replace("cadastre360_mineral_", "")}</div>
              <button onClick={() => toggle.mutate({ name: k, enabled: !v })}
                      className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm inline-flex items-center gap-1"
                      data-testid={"mineral-flag-" + k}>
                {v ? <ToggleRight size={11} /> : <ToggleLeft size={11} />} {v ? "on" : "off"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MineralGovernance = () => {
  const [tab, setTab] = useState("rights");
  return (
    <div className="p-6 space-y-4" data-testid="page-mineral-governance">
      <div className="flex items-start gap-3">
        <Pickaxe size={32} className="text-[#C99A2E]" />
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
            Cadastre360 · Phase V · National Mineral Governance · v1.5
          </div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-0.5">Mining Cadastre Programme</h1>
          <div className="text-[12.5px] text-[#B3C2CC] mt-1 max-w-4xl">
            Fourteen additive streams extending the existing Mining Cadastre into a full National Mineral Governance
            framework — internationally benchmarked, strictly additive, zero regression.
          </div>
        </div>
      </div>

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1 sticky top-2 z-10" data-testid="mineral-tabs">
        {STREAMS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={"mineral-tab-" + t.key}
                    className={"px-2.5 py-1.5 rounded-sm text-[10.5px] font-mono-cad uppercase tracking-[0.12em] inline-flex items-center gap-1.5 " +
                               (active ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
              <Icon size={11} className="text-[#C99A2E]" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "acceptance" ? <AcceptancePanel /> : <StreamPanel streamKey={tab} />}
    </div>
  );
};
